export interface PublicHoliday {
  date: string;
  name: string;
  country: "Moldova" | "USA";
}

// Preserve the calendars already used for historical requests.
const HISTORICAL_PUBLIC_HOLIDAYS: PublicHoliday[] = [
  // Moldova 2025
  { date: "2025-01-01", name: "New Year’s Day", country: "Moldova" },
  { date: "2025-01-07", name: "Orthodox Christmas", country: "Moldova" },
  { date: "2025-01-08", name: "Orthodox Christmas", country: "Moldova" },
  { date: "2025-03-08", name: "International Women’s Day", country: "Moldova" },
  { date: "2025-04-20", name: "Orthodox Easter", country: "Moldova" },
  { date: "2025-04-21", name: "Orthodox Easter Monday", country: "Moldova" },
  { date: "2025-04-28", name: "Memorial/Parents’ Day", country: "Moldova" },
  { date: "2025-05-01", name: "Labor Day", country: "Moldova" },
  { date: "2025-05-09", name: "Victory and Europe Day", country: "Moldova" },
  {
    date: "2025-06-01",
    name: "International Children’s Day",
    country: "Moldova",
  },
  { date: "2025-08-27", name: "Independence Day", country: "Moldova" },
  { date: "2025-08-31", name: "National Language Day", country: "Moldova" },
  {
    date: "2025-10-14",
    name: "Local Patron Saint Day (Hram)",
    country: "Moldova",
  },
  { date: "2025-12-25", name: "Western Christmas", country: "Moldova" },

  // Moldova 2026
  { date: "2026-01-01", name: "New Year’s Day", country: "Moldova" },
  { date: "2026-01-07", name: "Orthodox Christmas", country: "Moldova" },
  { date: "2026-01-08", name: "Orthodox Christmas", country: "Moldova" },
  { date: "2026-03-08", name: "International Women’s Day", country: "Moldova" },
  { date: "2026-04-12", name: "Orthodox Easter", country: "Moldova" },
  { date: "2026-04-13", name: "Orthodox Easter Monday", country: "Moldova" },
  { date: "2026-04-20", name: "Memorial/Parents’ Day", country: "Moldova" },
  { date: "2026-05-01", name: "Labor Day", country: "Moldova" },
  { date: "2026-05-09", name: "Victory and Europe Day", country: "Moldova" },
  {
    date: "2026-06-01",
    name: "International Children’s Day",
    country: "Moldova",
  },
  { date: "2026-08-27", name: "Independence Day", country: "Moldova" },
  { date: "2026-08-31", name: "National Language Day", country: "Moldova" },
  {
    date: "2026-10-14",
    name: "Local Patron Saint Day (Hram)",
    country: "Moldova",
  },
  { date: "2026-12-25", name: "Western Christmas", country: "Moldova" },

  // USA 2025
  { date: "2025-01-01", name: "New Year’s Day", country: "USA" },
  { date: "2025-05-26", name: "Memorial Day", country: "USA" },
  { date: "2025-07-04", name: "Independence Day", country: "USA" },
  { date: "2025-09-01", name: "Labor Day", country: "USA" },
  { date: "2025-11-27", name: "Thanksgiving Day", country: "USA" },
  { date: "2025-12-25", name: "Christmas Day", country: "USA" },

  // USA 2026
  { date: "2026-01-01", name: "New Year’s Day", country: "USA" },
  { date: "2026-05-25", name: "Memorial Day", country: "USA" },
  { date: "2026-07-04", name: "Independence Day", country: "USA" },
  { date: "2026-09-07", name: "Labor Day", country: "USA" },
  { date: "2026-11-26", name: "Thanksgiving Day", country: "USA" },
  { date: "2026-12-25", name: "Christmas Day", country: "USA" },
];

const holidaysByYear = new Map<number, readonly PublicHoliday[]>();

const dateString = (date: Date) => date.toISOString().slice(0, 10);

const nthWeekday = (
  year: number,
  month: number,
  weekday: number,
  occurrence: number,
) => {
  const first = new Date(Date.UTC(year, month - 1, 1));
  return new Date(
    Date.UTC(
      year,
      month - 1,
      1 + ((weekday - first.getUTCDay() + 7) % 7) + 7 * (occurrence - 1),
    ),
  );
};

const orthodoxEaster = (year: number) => {
  // Julian computus, converted to Gregorian with the century-specific offset.
  const d = (19 * (year % 19) + 15) % 30;
  const e = (2 * (year % 4) + 4 * (year % 7) - d + 34) % 7;
  const calendarDifference = Math.floor(year / 100) - Math.floor(year / 400) - 2;
  return new Date(Date.UTC(year, 2, 22 + d + e + calendarDifference));
};

const createPublicHolidays = (year: number): PublicHoliday[] => {
  const holidays = new Map<string, PublicHoliday>();
  const add = (date: Date, name: string, country: PublicHoliday["country"]) => {
    if (date.getUTCFullYear() !== year) return;
    const dateOnly = dateString(date);
    const key = `${country}:${dateOnly}`;
    const existing = holidays.get(key);
    // Coincident holidays occupy one calendar day, retaining both names.
    if (existing) {
      if (existing.name !== name) existing.name += ` / ${name}`;
    } else {
      holidays.set(key, { date: dateOnly, name, country });
    }
  };

  // Continue the existing Moldova calendar, including the local Hram day.
  // No automatic weekend substitution; government/office-specific transfers
  // require a separate decision and are not recurring national holidays.
  // https://www.legis.md/cautare/downloadpdf/142356
  const moldovaFixedDates: [number, number, string][] = [
    [1, 1, "New Year’s Day"],
    [1, 7, "Orthodox Christmas"],
    [1, 8, "Orthodox Christmas"],
    [3, 8, "International Women’s Day"],
    [5, 1, "Labor Day"],
    [5, 9, "Victory and Europe Day"],
    [6, 1, "International Children’s Day"],
    [8, 27, "Independence Day"],
    [8, 31, "National Language Day"],
    [10, 14, "Local Patron Saint Day (Hram)"],
    [12, 25, "Western Christmas"],
  ];
  moldovaFixedDates.forEach(([month, day, name]) =>
    add(new Date(Date.UTC(year, month - 1, day)), name, "Moldova"),
  );
  const easter = orthodoxEaster(year);
  add(easter, "Orthodox Easter", "Moldova");
  const easterHolidays: [number, string][] = [
    [1, "Orthodox Easter Monday"],
    [8, "Memorial/Parents’ Day"],
  ];
  easterHolidays.forEach(([offset, name]) => {
    const date = new Date(easter);
    date.setUTCDate(date.getUTCDate() + offset);
    add(date, name, "Moldova");
  });

  // Continue the existing six US holidays, including actual and observed dates.
  // https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/
  const addUSFixedHoliday = (
    holidayYear: number,
    month: number,
    day: number,
    name: string,
  ) => {
    const date = new Date(Date.UTC(holidayYear, month - 1, day));
    add(date, name, "USA");
    const weekday = date.getUTCDay();
    if (weekday === 6 || weekday === 0) {
      date.setUTCDate(date.getUTCDate() + (weekday === 6 ? -1 : 1));
      add(date, `${name} (observed)`, "USA");
    }
  };
  addUSFixedHoliday(year, 1, 1, "New Year’s Day");
  const lastMondayInMay = new Date(Date.UTC(year, 4, 31));
  lastMondayInMay.setUTCDate(31 - ((lastMondayInMay.getUTCDay() + 6) % 7));
  add(lastMondayInMay, "Memorial Day", "USA");
  addUSFixedHoliday(year, 7, 4, "Independence Day");
  add(nthWeekday(year, 9, 1, 1), "Labor Day", "USA");
  add(nthWeekday(year, 11, 4, 4), "Thanksgiving Day", "USA");
  addUSFixedHoliday(year, 12, 25, "Christmas Day");
  // A Saturday New Year is observed on December 31 of the selected year.
  if (year < 9999) addUSFixedHoliday(year + 1, 1, 1, "New Year’s Day");

  return Array.from(holidays.values()).sort((a, b) => a.date.localeCompare(b.date));
};

/** Returns holidays falling within the selected calendar year, in date-only form. */
export const getPublicHolidays = (
  year: number,
  country?: PublicHoliday["country"],
): readonly PublicHoliday[] => {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return [];
  let holidays = holidaysByYear.get(year);
  if (!holidays) {
    holidays =
      year < 2027
        ? HISTORICAL_PUBLIC_HOLIDAYS.filter(
            (holiday) => Number(holiday.date.slice(0, 4)) === year,
          )
        : createPublicHolidays(year);
    holidaysByYear.set(year, holidays);
  }
  return country ? holidays.filter((holiday) => holiday.country === country) : holidays;
};
